import {
  ENVIRONMENT_ACCESS_EXCHANGE_PATH,
  EnvironmentAccessExchangeResult,
  type CloudWorkspaceConnectOutcome,
  type CloudWorkspaceEndpoint,
} from "@afar-research/contracts";
import {
  ConnectError,
  connectDigest,
  signConnect,
  type ConnectFetch,
} from "@afar-research/shared/connect";
import * as Schema from "effect/Schema";

/**
 * Redeeming an introduction AFAR Connect brokered (ADR-0122, ADR-0150).
 *
 * Two steps that must stay in this order. AFAR Connect brokers an introduction and returns a
 * credential the destination minted and bound to this client's key; the client then redeems it
 * directly with the destination, through the Network Gateway. AFAR Connect never sees what comes
 * back, so it is a party to the introduction and to nothing after it.
 */

const decodeExchange = Schema.decodeUnknownSync(EnvironmentAccessExchangeResult);
const encodeJson = Schema.encodeSync(Schema.fromJsonString(Schema.Unknown));

/** The socket origin beside an endpoint's HTTP one. */
export function cloudWorkspaceWebSocketBaseUrl(httpBaseUrl: string): string {
  const url = new URL(httpBaseUrl);
  url.protocol = url.protocol === "http:" ? "ws:" : "wss:";
  return url.toString();
}

/**
 * Settles an introduction as data, so a refusal crosses a bridge with the code and status the
 * client classifies it by. Anything that is not a Connect refusal still throws.
 */
export async function cloudWorkspaceConnectOutcome(
  open: () => Promise<CloudWorkspaceEndpoint>,
  onRefused?: (cause: ConnectError) => void,
): Promise<CloudWorkspaceConnectOutcome> {
  try {
    return { _tag: "CloudWorkspaceOpened", endpoint: await open() };
  } catch (cause) {
    if (!(cause instanceof ConnectError)) throw cause;
    onRefused?.(cause);
    return { _tag: "CloudWorkspaceRefused", code: cause.code, status: cause.status };
  }
}

/**
 * Redeems a grant AFAR Connect brokered, directly with the Environment that minted it, proving
 * possession of this installation's key, for a Cloud Workspace and a Self-Hosted Machine alike.
 */
export async function redeemBrokeredGrant(input: {
  readonly fetch: ConnectFetch;
  readonly endpoint: string;
  readonly credential: string;
  readonly installation: { readonly publicKey: string; readonly privateKey: string };
  readonly clientLabel: string;
}): Promise<CloudWorkspaceEndpoint> {
  // Signed over the credential's digest rather than the credential, so the proof discloses
  // nothing on its own. It needs no nonce: what it accompanies is single-use.
  const claims = {
    operation: "exchange" as const,
    grantDigest: await connectDigest(input.credential),
    clientPublicKey: input.installation.publicKey,
  };
  const body = encodeJson({
    grant: input.credential,
    clientLabel: input.clientLabel,
    proof: { claims, signature: await signConnect(claims, input.installation.privateKey) },
  });
  // A machine Connect just introduced can still be joining its network, and a grant it brokered can
  // lapse on the way. Neither is a verdict on this client: the next attempt asks Connect for a new grant,
  // so every failure to redeem this one is retryable rather than a refusal of the account.
  const environmentFailure = (code: string, cause?: unknown) =>
    new ConnectError(code, 503, cause === undefined ? {} : { cause });
  const response = await input
    .fetch(
      new Request(`${input.endpoint}${ENVIRONMENT_ACCESS_EXCHANGE_PATH}`, {
        method: "POST",
        redirect: "error",
        signal: AbortSignal.timeout(30_000),
        headers: { "content-type": "application/json" },
        body,
      }),
    )
    .catch((cause: unknown) => {
      throw environmentFailure("environment_unreachable", cause);
    });
  if (!response.ok) throw environmentFailure(`environment_http_${String(response.status)}`);
  const exchanged = await response
    .json()
    .then(decodeExchange)
    .catch((cause: unknown) => {
      throw environmentFailure("environment_invalid_response", cause);
    });
  return {
    httpBaseUrl: input.endpoint,
    wsBaseUrl: cloudWorkspaceWebSocketBaseUrl(input.endpoint),
    session: exchanged.session,
    sessionId: exchanged.sessionId,
    sessionExpiresAt: exchanged.expiresAt,
  };
}
